import { edgelessToolbarI18nT } from '@blocksuite/affine-shared/services';
import {
  minimapControlAppearance,
  minimapControlBackground,
  minimapControlBorder,
  minimapControlBorderRadius,
  minimapControlBoxSizing,
  minimapControlColor,
  minimapControlFocusOutline,
  minimapControlFontFamily,
  minimapControlFontFeatureSettings,
  minimapControlFontSize,
  minimapControlFontStyle,
  minimapControlFontWeight,
  minimapControlGap,
  minimapControlHoverBackground,
  minimapControlHoverColor,
  minimapControlIconSize,
  minimapControlLineHeight,
  minimapControlMargin,
  minimapControlPadding,
  minimapControlSize,
  minimapControlTextAlign,
  minimapZoomPercentWidth,
} from '@blocksuite/affine-shared/styles';
import { MinusIcon, PlusIcon, ViewBarIcon } from '@blocksuite/icons/lit';
import { css, html, LitElement, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

/**
 * What the zoom bar steers: the canvas viewport or the workflow builder's
 * canvas. Each surface hands its own; the bar itself exists once.
 */
export interface ZoomControlsTarget {
  /** The zoom, 1 = 100 %. */
  readonly zoom: number;
  /** A locked view keeps its place: fit, zoom and 100 % do nothing. */
  readonly locked: boolean;
  /** Calls `onChange` when the zoom or the lock changes; returns the stop. */
  observe(onChange: () => void): () => void;
  fit(): void;
  zoomOut(): void;
  zoomIn(): void;
  /** 100 % around the same centre. */
  actualSize(): void;
  openOverview(): void;
}

export const ZOOM_CONTROLS_TAG = 'streamlain-zoom-controls';

/**
 * The zoom bar under a map: fit, zoom out, the zoom in percent (a click shows
 * 100 %), zoom in and the large overview.
 */
export class ZoomControls extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      /* Never wider than the map above it: on the narrowest dock (180) the four
         buttons and the zoom take 168, so the gaps shrink to what is left. */
      gap: clamp(
        0px,
        (
            100% - 4 * ${unsafeCSS(minimapControlSize)} -
              ${unsafeCSS(minimapZoomPercentWidth)}
          ) /
          4,
        ${unsafeCSS(minimapControlGap)}
      );
      width: 100%;
      fill: currentcolor;
    }

    .zoom-percent,
    .zoom-control-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${unsafeCSS(minimapControlSize)};
      height: ${unsafeCSS(minimapControlSize)};
      appearance: ${unsafeCSS(minimapControlAppearance)};
      margin: ${unsafeCSS(minimapControlMargin)};
      border: ${unsafeCSS(minimapControlBorder)};
      box-sizing: ${unsafeCSS(minimapControlBoxSizing)};
      padding: ${unsafeCSS(minimapControlPadding)};
      color: ${unsafeCSS(minimapControlColor)};
      background-color: ${unsafeCSS(minimapControlBackground)};
      border-radius: ${unsafeCSS(minimapControlBorderRadius)};
      cursor: pointer;
      white-space: nowrap;
      font-size: ${unsafeCSS(minimapControlFontSize)};
      font-style: ${unsafeCSS(minimapControlFontStyle)};
      font-weight: ${unsafeCSS(minimapControlFontWeight)};
      font-feature-settings: ${unsafeCSS(minimapControlFontFeatureSettings)};
      line-height: ${unsafeCSS(minimapControlLineHeight)};
      text-align: ${unsafeCSS(minimapControlTextAlign)};
      font-family: ${unsafeCSS(minimapControlFontFamily)};
    }

    .zoom-percent {
      width: ${unsafeCSS(minimapZoomPercentWidth)};
    }

    .zoom-control-button svg {
      width: ${unsafeCSS(minimapControlIconSize)};
      height: ${unsafeCSS(minimapControlIconSize)};
    }

    .zoom-percent:hover,
    .zoom-control-button:hover {
      color: ${unsafeCSS(minimapControlHoverColor)};
      background-color: ${unsafeCSS(minimapControlHoverBackground)};
    }

    .zoom-percent:focus-visible,
    .zoom-control-button:focus-visible {
      outline: ${unsafeCSS(minimapControlFocusOutline)};
      outline-offset: 2px;
    }

    .zoom-percent[disabled],
    .zoom-control-button[disabled] {
      pointer-events: none;
      cursor: not-allowed;
      color: var(--streamlain-v2-text-disable);
    }
  `;

  private _stopObserving: (() => void) | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this._observe();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._stopObserving?.();
    this._stopObserving = null;
  }

  override willUpdate(changed: Map<PropertyKey, unknown>) {
    if (changed.has('target') && this.isConnected) this._observe();
  }

  private _observe() {
    this._stopObserving?.();
    this._stopObserving =
      this.target?.observe(() => this.requestUpdate()) ?? null;
  }

  override render() {
    const target = this.target;
    if (!target) return html``;
    const locked = target.locked;
    const fit = edgelessToolbarI18nT('toolbar.zoomToFit');
    const zoomOut = edgelessToolbarI18nT('toolbar.zoomOut');
    const zoomIn = edgelessToolbarI18nT('toolbar.zoomIn');
    const overview = edgelessToolbarI18nT('toolbar.openCanvasOverview');
    return html`
      <button
        type="button"
        class="zoom-control-button"
        data-testid="zoom-fit"
        aria-label=${fit}
        title=${fit}
        ?disabled=${locked}
        @click=${() => target.fit()}
      >
        ${ViewBarIcon()}
      </button>
      <button
        type="button"
        class="zoom-control-button"
        data-testid="zoom-out"
        aria-label=${zoomOut}
        title=${zoomOut}
        ?disabled=${locked}
        @click=${() => target.zoomOut()}
      >
        ${MinusIcon()}
      </button>
      <button
        type="button"
        class="zoom-percent"
        data-testid="zoom-percent"
        ?disabled=${locked}
        @click=${() => target.actualSize()}
      >
        ${`${Math.round(target.zoom * 100)}%`}
      </button>
      <button
        type="button"
        class="zoom-control-button"
        data-testid="zoom-in"
        aria-label=${zoomIn}
        title=${zoomIn}
        ?disabled=${locked}
        @click=${() => target.zoomIn()}
      >
        ${PlusIcon()}
      </button>
      <button
        type="button"
        class="zoom-control-button"
        data-testid="zoom-overview"
        aria-label=${overview}
        title=${overview}
        aria-haspopup="dialog"
        aria-expanded=${this.overviewOpen ? 'true' : 'false'}
        @click=${() => target.openOverview()}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M3 3l6 6m12-6-6 6M3 21l6-6m12 6-6-6"
          />
        </svg>
      </button>
    `;
  }

  @property({ attribute: false })
  accessor target: ZoomControlsTarget | undefined;

  /** Whether the overview this bar opens is open. */
  @property({ type: Boolean, attribute: false })
  accessor overviewOpen = false;
}

/** Registers the bar once; every surface that shows it calls this. */
export function defineZoomControls() {
  if (!customElements.get(ZOOM_CONTROLS_TAG))
    customElements.define(ZOOM_CONTROLS_TAG, ZoomControls);
}

declare global {
  interface HTMLElementTagNameMap {
    [ZOOM_CONTROLS_TAG]: ZoomControls;
  }
}
